import { Box, Button, Grid, useTheme } from "@mui/material";
import { tokens } from "../../../../../../theme";
import { StatisticsCard } from "./StatisticsCard";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../../../service/Api";
import { showErrorToast } from "../../../../../../components/common/ShowToast";

interface StreamResult {
  approved_comments: number;
  total_comments: number;
  is_delivered: boolean;
  is_memory_book_available: boolean;
  assignees: { id: number }[];
}

interface CommentStats {
  total_comments: number;
  approved_comments: number;
  pending_comments: number;
}

interface Stats {
  total_memories: number;
  pending_review: number;
  approved: number;
  contacts_collected: number;
}

interface MemoryTableStatsProps {
  bookingId?: number;
  refresh?: number;
}

export const MemoryTableStats: React.FC<MemoryTableStatsProps> = ({
  bookingId,
  refresh,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [stats, setStats] = useState<Stats | null>(null);
  //const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!bookingId) return;
    setError(false);
    try {
      const [memoryBook, streams] = await Promise.all([
        apiService.get<{ count: number; results: StreamResult[] }>(
          `/api/streams/stream/memory-book/?booking=${bookingId}&page_size=100`,
        ),
        apiService.get<{ results: { id: number }[] }>(
          `/api/streams/stream/?booking=${bookingId}&page_size=100`,
        ),
      ]);

      const results = memoryBook.data.results;
      const streamIds = streams.data.results.map((s) => s.id);

      const messageResponses = await Promise.all(
        streamIds.map((id) =>
          apiService.get<{
            results: { sender_email: string | null; id: number }[];
          }>(`/api/chats/memorial-message/?stream_id=${id}&page_size=1000`),
        ),
      );
      const commentStatsResponses = await Promise.all(
        streamIds.map((id) =>
          apiService.get<CommentStats>(
            `/api/chats/memorial-message/comments-stats/?stream_id=${id}`,
          ),
        ),
      );

      const emailSet = new Set<string>();
      messageResponses.forEach((res) => {
        res.data.results.forEach((msg) => {
          const key = msg.sender_email?.toLowerCase() ?? `no-email-${msg.id}`;
          emailSet.add(key);
        });
      });

      setStats({
        total_memories: commentStatsResponses.reduce(
          (sum, res) => sum + res.data.total_comments,
          0
        ),
        pending_review: commentStatsResponses.reduce(
          (sum, res) => sum + res.data.pending_comments,
          0
        ),
        approved: commentStatsResponses.reduce(
          (sum, res) => sum + res.data.approved_comments,
          0
        ),
        contacts_collected: emailSet.size,
      });
    } catch {
      showErrorToast("Failed to load statistics.");
      setError(true);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, refresh]);

  // if (isLoading) {
  //   return (
  //     <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", bgcolor: colors.primary[900], width: "100%", height: 120, borderRadius: 2, mb: 3 }}>
  //       <CircularProgress size={25} />
  //     </Box>
  //   );
  // }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: colors.primary[900],
          width: "100%",
          height: 120,
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Button variant="contained" onClick={fetchStats}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Grid container spacing={1} columns={12} mb={3}>
      <StatisticsCard
        statisticFigure={stats?.total_memories ?? 0}
        statisticTitle="Total Memories"
      />
      <StatisticsCard
        statisticFigure={stats?.pending_review ?? 0}
        statisticTitle="Pending Review"
      />
      <StatisticsCard
        statisticFigure={stats?.approved ?? 0}
        statisticTitle="Approved"
      />
      <StatisticsCard
        statisticFigure={stats?.contacts_collected ?? 0}
        statisticTitle="Contacts collected"
      />
    </Grid>
  );
};
